import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import './RichTextEditor.css';

// Lightweight contenteditable-based editor with a small toolbar.
// Emits sanitized HTML via onChange(html).
export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Write a note…',
  className = '',
  onSubmit, // optional Ctrl+Enter handler
  minHeight,
}) {
  const editorRef = useRef(null);
  const [html, setHtml] = useState(value || '');

  useEffect(() => {
    setHtml(value || '');
  }, [value]);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== html) {
      editorRef.current.innerHTML = html || '';
    }
  }, [html]);

  const exec = (cmd, arg = null) => {
    // Ensure focus before executing commands
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    handleInput();
  };

  const handleInput = () => {
    const current = editorRef.current?.innerHTML || '';
    const clean = DOMPurify.sanitize(current, { USE_PROFILES: { html: true } });
    setHtml(clean);
    onChange && onChange(clean);
  };

  const handleKeyDown = (e) => {
    if (onSubmit && e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSubmit();
    }
  };

  const promptLink = () => {
    const url = window.prompt('Enter URL');
    if (!url) return;
    try {
      const a = document.createElement('a');
      a.href = url;
      const safeUrl = a.href; // normalizes
      exec('createLink', safeUrl);
    } catch (_) {
      // ignore invalid URLs
    }
  };

  const clearFormatting = () => {
    exec('removeFormat');
    // Also unwrap links
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const el = container.nodeType === 1 ? container : container.parentElement;
      if (!el) return;
      el.querySelectorAll('a').forEach((link) => {
        const parent = link.parentNode;
        while (link.firstChild) parent.insertBefore(link.firstChild, link);
        parent.removeChild(link);
      });
    }
    handleInput();
  };

  const containerStyle = {};
  if (minHeight) containerStyle.minHeight = minHeight;

  return (
    <div className={`rte-container ${className}`}> 
      <div className="rte-toolbar">
        <button type="button" className="rte-btn" onClick={() => exec('bold')}>B</button>
        <button type="button" className="rte-btn" onClick={() => exec('italic')}><em>I</em></button>
        <button type="button" className="rte-btn" onClick={() => exec('underline')}><u>U</u></button>
        <button type="button" className="rte-btn" onClick={() => exec('strikeThrough')}><s>S</s></button>
        <button type="button" className="rte-btn" onClick={() => exec('insertUnorderedList')}>• List</button>
        <button type="button" className="rte-btn" onClick={() => exec('insertOrderedList')}>1. List</button>
        <button type="button" className="rte-btn" onClick={() => exec('formatBlock', 'blockquote')}>❝ Quote</button>
        <button type="button" className="rte-btn" onClick={() => exec('formatBlock', 'h2')}>H2</button>
        <button type="button" className="rte-btn" onClick={promptLink}>Link</button>
        <button type="button" className="rte-btn" onClick={() => exec('insertHorizontalRule')}>HR</button>
        <button type="button" className="rte-btn" onClick={clearFormatting}>Clear</button>
      </div>
      <div
        ref={editorRef}
        className="rte-content"
        contentEditable
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        onInput={handleInput}
        onBlur={handleInput}
        onKeyDown={handleKeyDown}
        style={containerStyle}
        suppressContentEditableWarning
      />
      <div className="rte-footer">
        <span className="rte-hint">Ctrl/Cmd+Enter to submit</span>
      </div>
    </div>
  );
}

